import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import moment from "moment";
import swal from "sweetalert";
import "./ApprovalRequests.css";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import DataTable from "react-data-table-component";
import { Configuration } from "../../assets/data/config";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MonthlyReportDashboard from "./MonthlyReportDashboard";
import { useActivityLogger } from "../../hooks/useActivityLogger";
import { Box, Grid, Typography, MenuItem } from "@material-ui/core";
import { useGlobalSpinnerActionsContext } from "../../context/GlobalSpinnerContext";
import PerformanceReport from "./PerformanceReport";

function TabPanel({ children, value, index, isLoaded, ...other }) {
  if (!isLoaded) return null; // don't render until visited

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-panel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ display: value === index ? "block" : "none" }} // keep mounted
    >
      <Box p={2}>
        <Typography>{children}</Typography>
      </Box>
    </div>
  );
}

const ApprovalRequests = () => {
  const monthlyReport = useRef(null);
  const { sessionUserBoValue } = useActivityLogger();
  const setGlobalSpinner = useGlobalSpinnerActionsContext();

  const [approvalRequests, setApprovalRequests] = useState([]);
  const [viewDetails, setViewDetails] = useState(false);
  const [reportDetails, setReportDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loadedTabs, setLoadedTabs] = useState([0]); // load first tab initially
  const [updateDOM, setUpdateDOM] = useState(0);
  const [filters, setFilters] = useState({
    status: "All",
    facility: "All",
    period: "All",
  });
  const [formData, setFormData] = useState({
    mneStatus: "",
    mneRemarks: "",
    finalStatus: "",
    finalRemarks: "",
    period: "",
    facility_id: "",
  });

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
    if (!loadedTabs.includes(newValue)) {
      setLoadedTabs((prev) => [...prev, newValue]);
    }
  };

  useEffect(() => {
    // console.log("updateDOM", updateDOM);
    if (viewDetails && updateDOM && monthlyReport?.current) {
      monthlyReport.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [updateDOM, viewDetails]);

  const baseURL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:9000/mrunner_api"
      : `${window.location.protocol}//${window.location.host}/mrunner_api`;

  const getApprovalRequests = async () => {
    try {
      const response = await axios.get(`${baseURL}/getApprovalRequests`);
      setApprovalRequests(response.data);
    } catch (error) {
      console.log("Error in fetching the approval requests...");
    }
  };

  useEffect(() => {
    getApprovalRequests();
  }, []);

  const approveType = useMemo(() => {
    let type = null;
    if (
      Configuration.l1Approver.includes(
        sessionUserBoValue?.userCredentials?.userRoles[0].id
      )
    ) {
      type = "L1";
    }
    if (
      Configuration.l2Approver.includes(
        sessionUserBoValue?.userCredentials?.userRoles[0].id
      )
    ) {
      type = "L2";
    }
    return type;
  }, [sessionUserBoValue?.userCredentials?.userRoles]); //Don't remove ?

  const viewReportDetails = (data) => {
    setReportDetails(data);
    setFormData((prev) => ({
      ...prev,
      period: data.period || "",
      facility_id: data.facility_id || "",
      mneStatus: data.mne_status || "",
      mneRemarks: data.mne_remarks || "",
      finalStatus: data.final_status || "",
      finalRemarks: data.final_remarks || "",
    }));
    setViewDetails(true);
    setUpdateDOM((prev) => prev + 1);
  };

  const columns = useMemo(() => {
    const cols = [
      // {
      //   name: "#",
      //   selector: "id",
      //   sortable: true,
      //   wrap: true,
      // },
      {
        name: "Report Name",
        selector: (row) => row.report_name,
        sortable: true,
        wrap: true,
      },
      {
        name: "Created At",
        selector: (row) => row.created_at,
        sortable: true,
        wrap: true,
      },
      {
        name: "State",
        selector: (row) => row.state,
        sortable: true,
        wrap: true,
      },
      {
        name: "Facility",
        selector: (row) => row.facility,
        sortable: true,
        wrap: true,
      },
      {
        name: "Period",
        selector: (row) => row.period,
        sortable: true,
        wrap: true,
      },
      {
        name: "Submitted By",
        selector: (row) => row.submitted_by,
        sortable: true,
        wrap: true,
      },
      // {
      //   name: "Submitted At",
      //   selector: row => row.submitted_at,
      //   sortable: true,
      //   wrap: true,
      //   cell: (row) => (row.submitted_at ? row.submitted_at : "-"),
      // },
      {
        name: "M&E Approval Status",
        selector: row => row.mne_status,
        sortable: true,
        wrap: true,
        cell: (row) =>
          row.mne_status === "Approved"
            ? "Forward for Approval"
            : row.mne_status,
      },
      {
        name: "M&E Approval Remarks",
        selector: row => row.mne_remarks,
        sortable: true,
        wrap: true,
        cell: (row) => row?.mne_remarks || "-",
      },
      {
        name: "M&E Approval Date",
        selector: row => row.mne_approval_date,
        sortable: true,
        wrap: true,
        cell: (row) => (row.mne_approval_date ? row.mne_approval_date : "-"),
      },
      {
        name: "National Approval Status",
        selector: row => row.final_status,
        sortable: true,
        wrap: true,
      },
      {
        name: "National Approval Remarks",
        selector: row => row.final_remarks,
        sortable: true,
        wrap: true,
        cell: (row) => row?.final_remarks || "-",
      },
      {
        name: "National Approval Date",
        selector: row => row.final_approval_date,
        sortable: true,
        wrap: true,
        cell: (row) =>
          row.final_approval_date ? row.final_approval_date : "-",
      },
    ];

    // if (
    //   (Configuration.l1Approver.includes(
    //     sessionUserBoValue?.userCredentials?.userRoles[0].id
    //   ) ||
    //     Configuration.l2Approver.includes(
    //       sessionUserBoValue?.userCredentials?.userRoles[0].id
    //     )) &&
    //   approveType
    // ) {
    cols.push({
      name: "Action",
      cell: (row) => (
        <>
          <Button
            small
            color="primary"
            variant="contained"
            startIcon={<VisibilityIcon />}
            onClick={() => viewReportDetails(row)}
          >
            View
          </Button>
        </>
      ),
    });
    // }
    return cols;
  }, [sessionUserBoValue?.userCredentials?.userRoles, approveType]); //Don't remove ?

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updatedForm = {
      ...formData,
      [name]: value,
    };

    setFormData(updatedForm);
  };

  const handleSubmit = async (event) => {
    // console.log("formData", formData);
    event.preventDefault();
    const obj = {
      id: reportDetails.id,
      status: approveType === "L1" ? formData.mneStatus : formData.finalStatus,
      remarks:
        approveType === "L1" ? formData.mneRemarks : formData.finalRemarks,
      approval_date: moment().format("YYYY/MM/DD"),
      period: formData.period,
      facility_id: formData.facility_id,
      type: approveType,
    };

    try {
      setGlobalSpinner(true);
      await axios.post(`${baseURL}/approveRequest`, obj, {
        headers: { "Content-Type": "application/json" },
      });
      swal({
        title: "Success",
        text: `MPR ${obj.status} Successfully...`,
        icon: "success",
        button: "Close",
      });
      await axios.post(
        process.env.NODE_ENV === "development"
          ? `http://localhost:9000/mrunner_api/refreshDashboards`
          : `${window.location.protocol}//${window.location.host}/mrunner_api/refreshDashboards`,
        { r_id: 5 }
      );
    } catch (error) {
      console.log("Error while saving the data...", error);
      swal({
        title: "Error",
        text: "Error while saving the data...",
        icon: "error",
        button: "Close",
      });
    } finally {
      setGlobalSpinner(false);
      setReportDetails(null);
      setViewDetails(false);
      getApprovalRequests();
    }
  };

  const isMneApproval = useMemo(() => {
    const finalStatus = formData?.finalStatus;
    const mneStatus = formData?.mneStatus;

    const isEmpty = (val) =>
      val === null ||
      val === undefined ||
      val === "" ||
      (Array.isArray(val) && val.length === 0);

    if (
      approveType === "L1" &&
      finalStatus !== "Approved" &&
      !isEmpty(finalStatus)
    ) {
      return true;
    }

    if (approveType === "L2" && mneStatus === "Approved") {
      return true;
    }

    return false;
  }, [approveType, formData?.finalStatus, formData?.mneStatus]);

  const filteredRequests = useMemo(() => {
    // console.log(
    //   "sessionUserBoValue.displayName",
    //   sessionUserBoValue?.displayName
    // );
    // console.log("approvalRequests", approvalRequests);
    // console.log("filters", filters);

    // Step 1: Filter based on approveType
    let filtered = approveType
      ? approveType === "L1"
        ? approvalRequests
        : approvalRequests.filter((a) => a.mne_status === "Approved")
      : approvalRequests.filter(
          (a) => a.submitted_by === sessionUserBoValue?.displayName
        );
    return filtered;
  }, [approvalRequests, approveType, filters, sessionUserBoValue?.displayName]);

  const filteredApprovalRequests = useMemo(() => {
    // console.log("filteredRequests", filteredRequests);
    let filtered = [...filteredRequests];
    if (filters.status.length > 0 && filters.status !== "All") {
      filtered =
        approveType === "L1"
          ? filtered.filter((f) => f.mne_status === filters.status)
          : filtered.filter((f) => f.final_status === filters.status);
    }

    if (filters.facility.length > 0 && filters.facility !== "All") {
      filtered = filtered.filter(
        (f) =>
          f.facility === filters.facility || f.facility === filters.facility
      );
    }

    if (filters.period.length > 0 && filters.period !== "All") {
      filtered = filtered.filter(
        (f) => f.period === filters.period || f.period === filters.period
      );
    }
    // console.log("filtered", filtered);
    // Step 2: Sort by created_at (latest first)
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA; // descending
    });

    // console.log("sorted", sorted);
    return sorted;
  }, [filteredRequests, sessionUserBoValue?.displayName, filters]);

  // const updateStatus = (status) => {
  //   console.log("status", status);
  // };

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      const updatedData = {
        ...filters,
        [name]: value,
      };

      setFilters(updatedData);
    },
    [filters]
  );

  const actionsMemo = React.useMemo(() => {
    const facilityList = [
      ...new Set(filteredRequests.map((item) => item.facility)),
    ];
    const periodList = [
      ...new Set(filteredRequests.map((item) => item.period)),
    ];

    // console.log("facilityList", facilityList);
    // console.log("periodList", periodList);
    // If "All" exists, move it to the front
    const indexOfAll = facilityList.indexOf("All");
    if (indexOfAll > -1) {
      facilityList.splice(indexOfAll, 1); // remove "All"
      facilityList.unshift("All"); // add "All" to front
    }
    const indexOfAll1 = periodList.indexOf("All");
    if (indexOfAll1 > -1) {
      periodList.splice(indexOfAll1, 1); // remove "All"
      periodList.unshift("All"); // add "All" to front
    }
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "end",
          fontSize: "14px",
          gap: "10px",
        }}
      >
        <div style={{ width: "200px", marginBottom: "10px" }}>
          <label>Facility</label>
          <TextField
            select
            size="small"
            name="facility"
            value={filters.facility}
            onChange={handleFilterChange}
            variant="outlined"
          >
            <MenuItem value="All">All</MenuItem>
            {facilityList.map((f) => (
              <MenuItem value={f}>{f}</MenuItem>
            ))}
          </TextField>
        </div>
        <div style={{ width: "200px", marginBottom: "10px" }}>
          <label>Period</label>
          <TextField
            select
            size="small"
            name="period"
            value={filters.period}
            onChange={handleFilterChange}
            variant="outlined"
          >
            <MenuItem value="All">All</MenuItem>
            {periodList.map((p) => (
              <MenuItem value={p}>{p}</MenuItem>
            ))}
          </TextField>
        </div>
        <div style={{ width: "200px", marginBottom: "10px" }}>
          <label>Status</label>
          <TextField
            select
            size="small"
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            variant="outlined"
          >
            <MenuItem value="All">All</MenuItem>
            {approveType === "L1" && (
              <MenuItem value="Pending">Pending</MenuItem>
            )}
            <MenuItem value="Approved">
              {approveType === "L1" ? "Forward for Approval" : "Approved"}
            </MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </TextField>
        </div>
      </div>
    );
  }, [
    filters.status,
    filters.period,
    filters.facility,
    handleFilterChange,
    filteredRequests,
    approveType,
  ]);

  return (
    <div
      style={{
        padding: "65px 0px 0px",
        height: "calc(100vh - 64px)",
        width: "100%",
        position: "relative",
        overflowY: "auto",
      }}
    >
      <div className="inventoryTable">
        <DataTable
          title={
            approveType === "L1" || approveType === "L2"
              ? "Performance Report"
              : "Approval Requests"
          }
          columns={columns}
          data={filteredApprovalRequests}
          actions={actionsMemo}
          pagination
          highlightOnHover
          striped
          persistTableHead
          fixedHeader
        />
      </div>

      {viewDetails && (
        <div className="inventoryTable mt-30px" ref={monthlyReport}>
          {(approveType === "L1" || approveType === "L2") && (
            <form
              onSubmit={handleSubmit}
              className="inventoryForm"
              style={{ margin: 0 }}
            >
              <Grid container spacing={3} className="inventorySection">
                {(approveType === "L1" || approveType === "L2") && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Typography>
                        M&E Status<sup style={{ color: "red" }}>*</sup>
                      </Typography>
                      <TextField
                        select
                        name="mneStatus"
                        value={formData.mneStatus}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled={approveType === "L2"}
                      >
                        <MenuItem value="Approved">
                          Forward for Approval
                        </MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography>
                        M&E Remarks<sup style={{ color: "red" }}>*</sup>
                      </Typography>
                      <TextField
                        name="mneRemarks"
                        value={formData.mneRemarks}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled={approveType === "L2"}
                      ></TextField>
                    </Grid>
                  </>
                )}
                {approveType === "L2" && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Typography>
                        Deputy Director Status
                        <sup style={{ color: "red" }}>*</sup>
                      </Typography>
                      <TextField
                        select
                        name="finalStatus"
                        value={formData.finalStatus}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      >
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography>
                        Deputy Director Remarks
                        <sup style={{ color: "red" }}>*</sup>
                      </Typography>
                      <TextField
                        name="finalRemarks"
                        value={formData.finalRemarks}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      ></TextField>
                    </Grid>
                  </>
                )}

                {isMneApproval && (
                  <Grid item xs={12} sm={12} className="text-right">
                    <Typography>&nbsp;</Typography>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      style={{ width: "150px" }}
                    >
                      Submit
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>
          )}
          <Box key={updateDOM}>
            {approveType === "L1" || approveType === "L2" ? (
              <PerformanceReport reportDetails={reportDetails} />
            ) : (
              <MonthlyReportDashboard
                reportDetails={reportDetails}
                reportKey={6}
              />
            )}
            {/* <Tabs
              value={activeTab}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="approval tabs"
            >
              <Tab label="Line List" />
              <Tab label="Monthly Report" />
            </Tabs>

            <TabPanel
              value={activeTab}
              index={0}
              isLoaded={loadedTabs.includes(0)}
            >
              <LineListDashboard reportDetails={reportDetails} />
            </TabPanel>
            <TabPanel
              value={activeTab}
              index={1}
              isLoaded={loadedTabs.includes(1)}
            >
              <MonthlyReportDashboard reportDetails={reportDetails} />
            </TabPanel> */}
          </Box>
        </div>
      )}
    </div>
  );
};

export default ApprovalRequests;
